import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../services/api.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { toUtcIsoDate } from '../utils/date-utils';

@Component({
  standalone: true,
  selector: 'app-proforma',
  templateUrl: './proforma.component.html',
  styleUrl: './proforma.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatTableModule,
    MatCheckboxModule
  ]
})
export class ProformaComponent implements OnInit {
  proformaForm: FormGroup;
  unitTypes: string[] = [];
  orderStatuses: any[] = [];
  paymentTermsList: any[] = [];
  errorMessage: string = '';
  customerContacts: any[] = [];
  customerAddresses: any[] = [];
  customers: any[] = [];
  lotNumsList: any[] = [];
  filteredCustomers!: Observable<any[]>;
  filteredLotNumsList: Observable<any[]>[] = [];
  proformaId: number | null = null;
  isEditMode: boolean = false;
  salesOrders: any[] = [];
  salesOrderColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  purchaseOrders: any[] = [];
  purchaseOrderColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  salesInvoices: any[] = [];
  salesInvoiceColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  bankDetails: any = null;
  remitToAddress: any = null;
  purchaseOrderProductsList: any[] = [];
  filteredPurchaseOrderProductsList: Observable<any[]>[] = [];
  conversionError: string | null = null;
  isConverting: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {
    this.proformaForm = this.fb.group({
      // Proforma specific properties
      qboId: [null],
      proformaNumber: ['', Validators.required],
      proformaDate: [new Date(), Validators.required],
      dueDate: [null],
      status: ['Draft'],
      shipDate: [null],
      shipper: ['FedEx'],
      salesPerson: [''],

      // Customer Information
      customerId: [null, Validators.required],
      customerSearch: [''],
      shippingAddressId: [null],
      billingAddressId: [null],
      customerContactId: [null],
      customerPO: [''],
      shipAttentionTo: [''],

      // Related Sales Order
      salesOrderId: [null],

      // Financial Information
      subTotal: [0, [Validators.min(0)]],
      discountAmount: [0, [Validators.min(0)]],
      taxAmount: [0, [Validators.min(0)]],
      shippingAmount: [0, [Validators.min(0)]],
      handlingFee: [0, [Validators.min(0)]],
      totalAmount: [0, [Validators.min(0)]],
      amountPaid: [0, [Validators.min(0)]],

      // Payment Information
      paymentTerms: [''],
      paymentMethod: [''],
      terms: [''],

      // Additional Information
      notes: [''],

      // Bank Details
      bankDetails: this.fb.group({
        accountName: [''],
        bankName: [''],
        accountNumber: [''],
        routingNumber: [''],
        swiftCode: [''],
        fedwireABA: [''],
        ibanCode: [''],
        bankAddress: [''],
        isActive: [true],
        notes: ['']
      }),

      // Remit To Address
      remitToAddress: this.fb.group({
        addressLine1: [''],
        addressLine2: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: ['']
      }),

      // Products array
      products: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadUnitTypes();
    this.loadOrderStatuses();
    this.loadPaymentTerms();
    this.loadCustomers();
    this.loadLotNums();
    this.loadPurchaseOrderProducts();

    // Check if there's an id parameter in the route
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && !isNaN(+id)) {
        this.proformaId = +id;
        this.isEditMode = false;
        this.loadProforma(this.proformaId);
        this.loadRelatedSalesOrders(this.proformaId);
      } else {
        this.isEditMode = true;
        this.loadBankDetails();
        this.loadRemitToAddress();
        this.loadLatestOrderNumber();
      }
    });

    // Setup customer search autocomplete
    this.filteredCustomers = this.proformaForm.get('customerSearch')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCustomers(value || ''))
    );

    // Watch for customerId changes to load related contacts and addresses
    this.proformaForm.get('customerId')?.valueChanges.subscribe(customerId => {
      if (customerId) {
        this.proformaForm.patchValue({
          customerContactId: null,
          shippingAddressId: null,
          billingAddressId: null
        }, { emitEvent: false });
        this.loadCustomerContacts(customerId);
        this.loadCustomerAddresses(customerId);
      } else {
        this.customerContacts = [];
        this.customerAddresses = [];
        this.proformaForm.patchValue({
          customerContactId: null,
          shippingAddressId: null,
          billingAddressId: null
        }, { emitEvent: false });
      }
    });

    // Pre-populate from navigation state
    const prefill = history.state?.prefill;
    if (prefill) {
      if (prefill.salesOrder && !this.salesOrders.find(so => so.id === prefill.salesOrder?.id)) {
        this.salesOrders.push(prefill.salesOrder);
      }

      if (prefill.purchaseOrders?.length) {
        prefill.purchaseOrders.forEach((po: any) => {
          if (!this.purchaseOrders.find((p: any) => p.id === po.id)) {
            this.purchaseOrders.push(po);
          }
        });
      }

      this.proformaForm.patchValue({
        customerId: prefill.customerId || null,
        salesOrderId: prefill.salesOrderId || null,
        customerContactId: prefill.customerContactId || null,
        shippingAddressId: prefill.shippingAddressId || null,
        billingAddressId: prefill.billingAddressId || null,
        customerPO: prefill.customerPO || '',
        shipAttentionTo: prefill.shipAttentionTo || '',
        shipper: prefill.shipper || 'FedEx',
        salesPerson: prefill.salesPerson || '',
        paymentTerms: prefill.paymentTerms || '',
        discountAmount: prefill.discountAmount || 0,
        taxAmount: prefill.taxAmount || 0,
        shippingAmount: prefill.shippingAmount || 0,
        handlingFee: prefill.handlingFee || 0,
        ...(prefill.customerSearch ? { customerSearch: prefill.customerSearch } : {})
      }, { emitEvent: false });

      if (prefill.products?.length) {
        prefill.products.forEach((p: any) => {
          const type = p.purchaseOrderProductId ? 'purchaseOrderProduct' : 'lotNum';
          const group = this.createProductLineItem(type);
          group.patchValue({
            purchaseOrderProductId: p.purchaseOrderProductId || '',
            poNumber: p.poNumber || '',
            lotNumId: p.lotNumId || '',
            productId: p.productId || '',
            unitPrice: p.unitPrice || 0,
            unitType: p.unitType || '',
            quantity: p.quantity || 1,
            description: p.description || '',
            desiredPackaging: p.desiredPackaging || ''
          });
          // Seed the PO product display until the pending inventory list enriches it
          if (type === 'purchaseOrderProduct' && p.poNumber) {
            group.patchValue({ purchaseOrderProductSearch: { purchaseOrder: { number: p.poNumber } } }, { emitEvent: false });
          }
          this.products.push(group);
          const productIndex = this.products.length - 1;
          if (type === 'purchaseOrderProduct') {
            this.setupPurchaseOrderProductAutocomplete(productIndex);
          } else {
            this.setupLotNumAutocomplete(productIndex);
          }
        });
        this.enrichPurchaseOrderLineItems();
        this.enrichLotNumLineItems();
      }

      if (prefill.customerId) {
        this.loadCustomerContacts(prefill.customerId);
        this.loadCustomerAddresses(prefill.customerId);
      }
    }
  }

  enterEditMode(): void {
    this.isEditMode = true;
    this.setSelectsDisabled(false);
  }

  private showError(message: string, err?: any): void {
    let detail: string | undefined;
    if (err?.error) {
      // err.error is the parsed response body
      if (typeof err.error === 'string') {
        detail = err.error;
      } else {
        detail = err.error.detail || err.error.message || err.error.title || err.error.error;
      }
    }
    // Fall back to HTTP status text only (not err.message which includes the full URL)
    if (!detail && err?.statusText && err.statusText !== 'OK') {
      detail = `(${err.status}: ${err.statusText})`;
    }
    this.errorMessage = detail ? `${message} ${detail}` : message;
    this.cdr.detectChanges();
  }

  private setSelectsDisabled(disabled: boolean): void {
    const controls = ['status', 'shippingAddressId', 'billingAddressId', 'customerContactId', 'paymentTerms', 'paymentMethod'];
    controls.forEach(ctrl => {
      const control = this.proformaForm.get(ctrl);
      disabled ? control?.disable({ emitEvent: false }) : control?.enable({ emitEvent: false });
    });
    this.products.controls.forEach(product => {
      const unitType = product.get('unitType');
      disabled ? unitType?.disable({ emitEvent: false }) : unitType?.enable({ emitEvent: false });
    });
  }

  loadProforma(id: number): void {
    while (this.products.length) this.products.removeAt(0);
    this.apiService.get<any>(`proforma/${id}`).subscribe({
      next: (data) => {
        console.log('Proforma data received:', data);

        const proforma = {

          proformaNumber: data.number || data.Number || '',
          proformaDate: data.date || data.Date ? new Date(data.date || data.Date) : new Date(),
          dueDate: data.dueDate || data.DueDate ? new Date(data.dueDate || data.DueDate) : null,
          status: data.status || data.Status || 'Draft',
          shipDate: data.shipDate || data.ShipDate ? new Date(data.shipDate || data.ShipDate) : null,
          shipper: data.shipper || data.Shipper || '',
          salesPerson: data.salesPerson || data.SalesPerson || '',
          shipAttentionTo: data.shipAttentionTo || data.ShipAttentionTo || '',
          customerId: data.customerId || data.CustomerId || null,
          shippingAddressId: data.shippingAddressId || data.ShippingAddressId || null,
          billingAddressId: data.billingAddressId || data.BillingAddressId || null,
          customerContactId: data.customerContactId || data.CustomerContactId || null,
          customerPO: data.customerPO || data.CustomerPO || '',
          salesOrderId: data.salesOrderId || data.SalesOrderId || null,
          subTotal: data.subTotal || data.SubTotal || 0,
          discountAmount: data.discountAmount || data.DiscountAmount || 0,
          taxAmount: data.taxAmount || data.TaxAmount || 0,
          shippingAmount: data.shippingAmount || data.ShippingAmount || 0,
          handlingFee: data.handlingFee || data.HandlingFee || 0,
          totalAmount: data.totalAmount || data.TotalAmount || 0,
          amountPaid: data.amountPaid || data.AmountPaid || 0,
          paymentTerms: data.paymentTerms || data.PaymentTerms || '',
          paymentMethod: data.paymentMethod || data.PaymentMethod || '',
          terms: data.terms || data.Terms || '',
          notes: data.externalNotes || data.ExternalNotes || ''
        };

        const customerContact = data.customerContact || data.CustomerContact;
        const customerAddress = data.customerAddress || data.CustomerAddress;
        const customerObj = data.customer || data.Customer;
        const bankDetailsObj = data.bankDetails || data.BankDetails;
        const remitToAddressObj = data.remitToAddress || data.RemitToAddress;

        if (customerContact) this.customerContacts = [customerContact];
        if (customerAddress) this.customerAddresses = [customerAddress];

        if (bankDetailsObj) {
          this.bankDetails = bankDetailsObj;
          this.proformaForm.patchValue({
            bankDetails: {
              id: bankDetailsObj.id || bankDetailsObj.Id || '',
              accountName: bankDetailsObj.accountName || bankDetailsObj.AccountName || '',
              bankName: bankDetailsObj.bankName || bankDetailsObj.BankName || '',
              accountNumber: bankDetailsObj.accountNumber || bankDetailsObj.AccountNumber || '',
              routingNumber: bankDetailsObj.routingNumber || bankDetailsObj.RoutingNumber || '',
              swiftCode: bankDetailsObj.swiftCode || bankDetailsObj.SwiftCode || '',
              fedwireABA: bankDetailsObj.fedwireABA || bankDetailsObj.FedwireABA || '',
              ibanCode: bankDetailsObj.ibanCode || bankDetailsObj.IBANCode || '',
              bankAddress: bankDetailsObj.bankAddress || bankDetailsObj.BankAddress || '',
              isActive: bankDetailsObj.isActive !== undefined ? bankDetailsObj.isActive : (bankDetailsObj.IsActive !== undefined ? bankDetailsObj.IsActive : true),
              notes: bankDetailsObj.notes || bankDetailsObj.Notes || ''
            }
          }, { emitEvent: false });
        }

        if (remitToAddressObj) {
          this.remitToAddress = remitToAddressObj;
          this.proformaForm.patchValue({
            remitToAddress: {
              addressLine1: remitToAddressObj.addressLine1 || remitToAddressObj.AddressLine1 || '',
              addressLine2: remitToAddressObj.addressLine2 || remitToAddressObj.AddressLine2 || '',
              city: remitToAddressObj.city || remitToAddressObj.City || '',
              state: remitToAddressObj.state || remitToAddressObj.State || '',
              postalCode: remitToAddressObj.postalCode || remitToAddressObj.PostalCode || '',
              country: remitToAddressObj.country || remitToAddressObj.Country || ''
            }
          }, { emitEvent: false });
        }

        this.proformaForm.patchValue(proforma, { emitEvent: false });

        if (proforma.customerId) {
          if (customerObj) {
            this.proformaForm.patchValue({ customerSearch: customerObj }, { emitEvent: false });
          } else {
            const customer = this.customers.find(c => (c.id || c.Id) === proforma.customerId);
            if (customer) {
              this.proformaForm.patchValue({ customerSearch: customer }, { emitEvent: false });
            } else {
              this.apiService.get<any>(`customer/${proforma.customerId}`).subscribe({
                next: (customerData) => {
                  this.proformaForm.patchValue({ customerSearch: customerData }, { emitEvent: false });
                },
                error: (err) => console.error('Error loading customer:', err)
              });
            }
          }
        }

        if (proforma.customerId && (!customerContact || !customerAddress)) {
          if (!customerContact) this.loadCustomerContacts(proforma.customerId);
          if (!customerAddress) this.loadCustomerAddresses(proforma.customerId);
        }

        const productsData = data.products || data.Products || [];
        productsData.forEach((product: any) => {
          const purchaseOrderProductId = product.purchaseOrderProductId || product.PurchaseOrderProductId || '';
          const lotNumId = product.lotNumId || product.LotNumId || '';
          const type: 'purchaseOrderProduct' | 'lotNum' = purchaseOrderProductId ? 'purchaseOrderProduct' : (lotNumId ? 'lotNum' : 'purchaseOrderProduct');
          const group = this.createProductLineItem(type);
          group.patchValue({
            purchaseOrderProductId,
            lotNumId,
            productId: product.productId || product.ProductId || '',
            catalogNum: product.catalogNum || product.CatalogNum || '',
            unitPrice: product.unitPrice || product.UnitPrice || 0,
            unitType: product.unitType || product.UnitType || '',
            quantity: product.quantity || product.Quantity || 1,
            lineNumber: product.lineNumber || product.LineNumber || 0,
            discountPercent: product.discountPercent || product.DiscountPercent || 0,
            discountAmount: product.discountAmount || product.DiscountAmount || 0,
            taxRate: product.taxRate || product.TaxRate || 0,
            taxAmount: product.taxAmount || product.TaxAmount || 0,
            description: product.description || product.Description || '',
            notes: product.notes || product.Notes || '',
            taxCode: product.taxCode || product.TaxCode || '',
            desiredPackaging: product.desiredPackaging || product.DesiredPackaging || ''
          }, { emitEvent: false });
          // The API embeds the PurchaseOrderProduct (with PurchaseOrder, Product/VendorProduct and LotNum),
          // so enrich from it directly - it is the only source once a lot number has been assigned.
          const embeddedPop = product.purchaseOrderProduct || product.PurchaseOrderProduct;
          if (type === 'purchaseOrderProduct' && embeddedPop) {
            this.applyPurchaseOrderProductToLineItem(group, embeddedPop);
          }
          this.products.push(group);
          const productIndex = this.products.length - 1;
          if (type === 'purchaseOrderProduct') {
            this.setupPurchaseOrderProductAutocomplete(productIndex);
          } else {
            this.setupLotNumAutocomplete(productIndex);
          }
        });
        this.enrichPurchaseOrderLineItems();
        this.enrichLotNumLineItems();
        this.setSelectsDisabled(true);
      },
      error: (error) => {
        console.error('Error loading proforma:', error);
        this.showError('Failed to load proforma. Please try again.', error);
      }
    });
  }

  loadUnitTypes(): void {
    this.apiService.get<string[]>('product/unitTypes').subscribe({
      next: (data) => { this.unitTypes = data; },
      error: (err) => { console.error('Error loading unit types:', err); this.unitTypes = []; }
    });
  }

  loadLatestOrderNumber(): void {
    this.apiService.get<any>(`invoice/latest-number`).subscribe({
      next: (data) => {
        this.proformaForm.patchValue({ proformaNumber: data.orderNumber }, { emitEvent: false });
      },
      error: (err) => {
        console.error('Error loading latest order number:', err);
        this.proformaForm.patchValue({ proformaNumber: '' }, { emitEvent: false });
      }
    });
  }

  loadPurchaseOrderProducts(): void {
    this.apiService.get<any[]>('purchaseorder/pending-inventory').subscribe({
      next: (data) => {
        this.purchaseOrderProductsList = data || [];
        this.enrichPurchaseOrderLineItems();
      },
      error: (err) => { console.error('Error loading purchase order products:', err); this.purchaseOrderProductsList = []; }
    });
  }

  // Maps a PurchaseOrderProduct (from the API payload or the pending inventory list) onto a line item.
  // Existing form values win where the source is missing data, so partial sources never blank out fields.
  private applyPurchaseOrderProductToLineItem(control: AbstractControl, pop: any): void {
    if (!pop) return;
    control.patchValue({
      purchaseOrderProductSearch: pop,
      poNumber: pop.purchaseOrder?.number || pop.purchaseOrder?.Number || control.get('poNumber')?.value || '',
      productId: pop.vendorProduct?.product?.id || pop.vendorProduct?.product?.Id || pop.product?.id || pop.product?.Id || control.get('productId')?.value || '',
      productName: pop.vendorProduct?.product?.name || pop.vendorProduct?.product?.Name || pop.product?.name || pop.product?.Name || control.get('productName')?.value || '',
      catalogNum: pop.vendorProduct?.product?.catalogNum || pop.vendorProduct?.product?.CatalogNum || pop.product?.catalogNum || pop.product?.CatalogNum || control.get('catalogNum')?.value || '',
      vendorCatalogNum: pop.vendorProduct?.catalogNum || pop.vendorProduct?.CatalogNum || control.get('vendorCatalogNum')?.value || '',
      vendorLotNum: pop.lotNum?.vendorLotNum || pop.LotNum?.VendorLotNum || control.get('vendorLotNum')?.value || '',
      // Display only - the assigned lot lives on the PurchaseOrderProduct, so it is deliberately
      // not written back to InvoiceProduct.LotNumId (that stays derivable via PurchaseOrderProductId).
      lotNumber: pop.lotNum?.number || pop.LotNum?.Number || control.get('lotNumber')?.value || '',
      unitType: pop.unitType || pop.UnitType || control.get('unitType')?.value || '',
      quantityAvailable: pop.quantityAvailable ?? pop.QuantityAvailable ?? control.get('quantityAvailable')?.value ?? null,
      description: control.get('description')?.value || pop.vendorProduct?.product?.description || pop.vendorProduct?.product?.Description || pop.product?.description || pop.product?.Description || ''
    }, { emitEvent: false });
  }

  // Fallback enrichment for line items whose PO product was not embedded in the payload.
  // Note: PO products that already have a lot number assigned are absent from the pending
  // inventory list, so those lines must be enriched from the payload instead.
  private enrichPurchaseOrderLineItems(): void {
    if (!this.purchaseOrderProductsList.length) return;
    this.products.controls.forEach((control) => {
      const popId = control.get('purchaseOrderProductId')?.value;
      if (!popId) return;
      const current = control.get('purchaseOrderProductSearch')?.value;
      if (current && typeof current === 'object' && (current.id || current.Id) == popId) return;
      const found = this.purchaseOrderProductsList.find(pop => (pop.id || pop.Id) == popId);
      if (found) {
        this.applyPurchaseOrderProductToLineItem(control, found);
      }
    });
  }

  loadOrderStatuses(): void {
    this.apiService.get<string[]>('misc/orderStatuses').subscribe({
      next: (data) => { this.orderStatuses = data; },
      error: (err) => { console.error('Error loading order statuses:', err); this.orderStatuses = []; }
    });
  }

  loadPaymentTerms(): void {
    this.apiService.get<string[]>('misc/paymentTerms').subscribe({
      next: (data) => {
        this.paymentTermsList = data || [];
        if (!this.proformaId && !this.proformaForm.get('paymentTerms')?.value) {
          const defaultTerm = this.paymentTermsList.find((t: any) => (t.name || t.Name) === 'Net30');
          if (defaultTerm) {
            this.proformaForm.patchValue({ paymentTerms: defaultTerm.name || defaultTerm.Name }, { emitEvent: false });
          }
        }
      },
      error: (err) => {
        console.error('Error loading payment terms:', err);
        this.paymentTermsList = [];
      }
    });
  }

  loadCustomers(): void {
    this.apiService.get<any[]>('customer/all').subscribe({
      next: (data) => { this.customers = data || []; },
      error: (err) => { console.error('Error loading customers:', err); this.customers = []; }
    });
  }

  private _filterCustomers(value: string): any[] {
    if (typeof value !== 'string') return this.customers;
    const filterValue = value.toLowerCase();
    return this.customers.filter(customer => {
      const name = customer.name || customer.Name || '';
      const id = (customer.id || customer.Id || '').toString();
      return name.toLowerCase().includes(filterValue) || id.includes(filterValue);
    });
  }

  displayCustomer(customer: any): string {
    return customer ? `${customer.name || customer.Name || ''} (ID: ${customer.id || customer.Id || ''})` : '';
  }

  onCustomerSelected(customer: any): void {
    if (customer) {
      const customerId = customer.id || customer.Id;
      this.proformaForm.patchValue({ customerId: customerId });
    }
  }

  clearCustomer(): void {
    this.proformaForm.patchValue({
      customerSearch: '',
      customerId: null,
      customerContactId: null,
      shippingAddressId: null,
      billingAddressId: null
    });
    this.customerContacts = [];
    this.customerAddresses = [];
  }

  loadCustomerContacts(customerId: string | number): void {
    this.apiService.get<any[]>(`contact/customer/all/${customerId}`).subscribe({
      next: (data) => { this.customerContacts = data || []; this.cdr.detectChanges(); },
      error: (err) => { console.error('Error loading customer contacts:', err); this.customerContacts = []; }
    });
  }

  loadCustomerAddresses(customerId: string | number): void {
    this.apiService.get<any[]>(`address/customer/all/${customerId}`).subscribe({
      next: (data) => { this.customerAddresses = data || []; this.cdr.detectChanges(); },
      error: (err) => { console.error('Error loading customer addresses:', err); this.customerAddresses = []; }
    });
  }

  loadLotNums(): void {
    this.apiService.get<any[]>('product/lotnums/all').subscribe({
      next: (data) => { this.lotNumsList = data || []; this.enrichLotNumLineItems(); },
      error: (err) => { console.error('Error loading lot numbers:', err); this.lotNumsList = []; }
    });
  }

  private enrichLotNumLineItems(): void {
    if (!this.lotNumsList.length) return;
    this.products.controls.forEach((control) => {
      if (control.get('lineItemType')?.value !== 'lotNum') return;
      const lotNumId = control.get('lotNumId')?.value;
      if (!lotNumId) return;
      const found = this.lotNumsList.find(ln => (ln.id || ln.Id) == lotNumId);
      if (found) {
        control.patchValue({
          lotNumSearch: found,
          productId: found.productId || found.ProductId || found.vendorProduct?.product?.id || found.product?.id || found.product?.Id || '',
          productName: found.vendorProduct?.product?.name || found.vendorProduct?.product?.Name || found.product?.name || found.product?.Name || '',
          catalogNum: found.vendorProduct?.product?.catalogNum || found.vendorProduct?.product?.CatalogNum || found.product?.catalogNum || found.product?.CatalogNum || '',
          vendorCatalogNum: found.vendorProduct?.catalogNum || found.VendorProduct?.CatalogNum || '',
          vendorLotNum: found.vendorLotNum || found.VendorLotNum || '',
          unitType: found.unitType || found.UnitType || control.get('unitType')?.value || '',
          quantityAvailable: found.quantityOnHand ?? found.QuantityOnHand ?? found.quantityAvailable ?? found.QuantityAvailable ?? null,
          // Preserve an existing line-item description and only fall back to the master product description
          description: control.get('description')?.value || found.vendorProduct?.product?.description || found.vendorProduct?.product?.Description || found.product?.description || found.product?.Description || ''
        }, { emitEvent: false });
      }
    });
  }

  setupLotNumAutocomplete(index: number): void {
    const control = this.products.at(index).get('lotNumSearch')!;
    this.filteredLotNumsList[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map(value => this._filterLotNums(value || ''))
    );
  }

  _filterLotNums(value: string | any): any[] {
    if (typeof value !== 'string') return this.lotNumsList;
    const f = value.toLowerCase();
    return this.lotNumsList.filter(ln => {
      const num        = (ln.number  || ln.Number  || '').toLowerCase();
      const catalog    = (ln.vendorProduct?.product?.catalogNum || ln.vendorProduct?.product?.CatalogNum || ln.product?.catalogNum || ln.product?.CatalogNum || '').toLowerCase();
      const name       = (ln.vendorProduct?.product?.name || ln.vendorProduct?.product?.Name || ln.product?.name || ln.product?.Name || '').toLowerCase();
      const vendorName = (ln.vendorProduct?.vendor?.name || ln.vendorProduct?.vendor?.Name || '').toLowerCase();
      return num.includes(f) || catalog.includes(f) || name.includes(f) || vendorName.includes(f);
    });
  }

  displayLotNum(lotNum: any): string {
    return lotNum ? (lotNum.number || lotNum.Number || '') : '';
  }

  onLotNumSelected(index: number, lotNum: any): void {
    const lotNumId         = lotNum.id || lotNum.Id;
    const productId        = lotNum.productId || lotNum.ProductId || lotNum.vendorProduct?.product?.id || lotNum.product?.id || lotNum.product?.Id || '';
    const catalogNum       = lotNum.vendorProduct?.product?.catalogNum || lotNum.vendorProduct?.product?.CatalogNum || lotNum.product?.catalogNum || lotNum.product?.CatalogNum || '';
    const productName      = lotNum.vendorProduct?.product?.name || lotNum.vendorProduct?.product?.Name || lotNum.product?.name || lotNum.product?.Name || '';
    const vendorCatalogNum = lotNum.vendorProduct?.catalogNum || lotNum.vendorProduct?.CatalogNum || '';
    const vendorLotNum     = lotNum.vendorLotNum || lotNum.VendorLotNum || '';
    const unitType         = lotNum.unitType || lotNum.UnitType || '';
    const quantityAvailable = lotNum.quantityOnHand ?? lotNum.QuantityOnHand ?? lotNum.quantityAvailable ?? lotNum.QuantityAvailable ?? null;
    const description      = lotNum.vendorProduct?.product?.description || lotNum.vendorProduct?.product?.Description || lotNum.product?.description || lotNum.product?.Description || '';
    this.products.at(index).patchValue({ lotNumId, purchaseOrderProductId: '', poNumber: '', productId, catalogNum, productName, vendorCatalogNum, vendorLotNum, unitType, quantityAvailable, description });
  }

  clearLotNum(index: number): void {
    this.products.at(index).patchValue({
      lotNumSearch: '', lotNumId: '', productId: '', productName: '',
      catalogNum: '', vendorCatalogNum: '', vendorLotNum: '',
      quantityAvailable: null, description: ''
    });
  }

  getLotNumLabel(id: any): string {
    if (!id) return '';
    const found = this.lotNumsList.find(ln => (ln.id || ln.Id) == id);
    return found ? (found.number || found.Number || '') : '';
  }

  get products(): FormArray {
    return this.proformaForm.get('products') as FormArray;
  }

  getProductTitle(index: number): string {
    const item = this.products.at(index).value;
    const type = item.lineItemType;
    const quantity = item.quantity || 0;
    const unitType = item.unitType || 'each';
    const unitPrice = item.unitPrice || 0;
    const total = this.calculateLineTotalWithTax(index);
    const catalogNum = item.catalogNum || '';
    const productName = item.productName || '';

    let identifier = '';
    if (type === 'lotNum') {
      const search = item.lotNumSearch;
      identifier = search
        ? (typeof search === 'object' ? (search.number || search.Number || '') : String(search))
        : (item.lotNumId ? `Lot ${item.lotNumId}` : '');
    } else {
      const search = item.purchaseOrderProductSearch;
      identifier = search
        ? (typeof search === 'object' ? this.displayPurchaseOrderProduct(search) : String(search))
        : (item.poNumber || (item.purchaseOrderProductId ? `PO Product ${item.purchaseOrderProductId}` : ''));
    }

    const label = identifier
      ? (productName && !identifier.includes(productName) ? `${identifier} — ${productName}` : identifier)
      : catalogNum;

    if (label && quantity) {
      return `${label} - ${quantity} ${unitType} @ $${unitPrice} = $${total.toFixed(2)}`;
    } else if (label) {
      return label;
    }
    return `Product Line ${index + 1}`;
  }

  createProductLineItem(type: 'purchaseOrderProduct' | 'lotNum' = 'purchaseOrderProduct'): FormGroup {
    return this.fb.group({
      lineItemType: [type],
      purchaseOrderProductId: [''],
      purchaseOrderProductSearch: [''],
      poNumber: [''],
      lotNumId: [''],
      lotNumber: [''],
      lotNumSearch: [''],
      productId: ['', Validators.required],
      productName: [''],
      catalogNum: [''],
      vendorCatalogNum: [''],
      vendorLotNum: [''],
      quantityAvailable: [null],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      unitType: [''],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      lineNumber: [this.products.length + 1],
      discountPercent: [0, [Validators.min(0), Validators.max(100)]],
      discountAmount: [0, [Validators.min(0)]],
      taxRate: [0, [Validators.min(0)]],
      taxAmount: [0, [Validators.min(0)]],
      description: [''],
      notes: [''],
      taxCode: [''],
      desiredPackaging: ['']
    });
  }

  addPurchaseOrderProduct(): void {
    this.products.push(this.createProductLineItem('purchaseOrderProduct'));
    this.setupPurchaseOrderProductAutocomplete(this.products.length - 1);
  }

  addLotNumProduct(): void {
    this.products.push(this.createProductLineItem('lotNum'));
    this.setupLotNumAutocomplete(this.products.length - 1);
  }

  isQuantityExceeded(index: number): boolean {
    const product = this.products.at(index);
    const quantityAvailable = product.get('quantityAvailable')?.value;
    if (quantityAvailable === null || quantityAvailable === undefined) return false;
    const quantity = product.get('quantity')?.value || 0;
    return quantity > quantityAvailable;
  }

  removeProduct(index: number): void {
    this.products.removeAt(index);
    this.updateLineNumbers();
  }

  updateLineNumbers(): void {
    this.products.controls.forEach((control, index) => {
      control.get('lineNumber')?.setValue(index + 1);
    });
  }

  calculateLineTotal(index: number): number {
    const product = this.products.at(index);
    const unitPrice = product.get('unitPrice')?.value || 0;
    const quantity = product.get('quantity')?.value || 0;
    return unitPrice * quantity;
  }

  setupPurchaseOrderProductAutocomplete(index: number): void {
    const control = this.products.at(index).get('purchaseOrderProductSearch')!;
    this.filteredPurchaseOrderProductsList[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map(value => this._filterPurchaseOrderProducts(value || ''))
    );
  }

  _filterPurchaseOrderProducts(value: string | any): any[] {
    // Only offer pending inventory: PO products without a lot number assigned yet
    const source = this.purchaseOrderProductsList.filter(pop =>
      !(pop.lotNumId || pop.LotNumId || pop.lotNum?.id || pop.LotNum?.Id));
    if (typeof value !== 'string') return source;
    const f = value.toLowerCase();
    return source.filter(pop => {
      const poNum = (pop.purchaseOrder?.number || pop.purchaseOrder?.Number || '').toLowerCase();
      const catalog = (pop.vendorProduct?.product?.catalogNum || pop.vendorProduct?.product?.CatalogNum || pop.product?.catalogNum || pop.product?.CatalogNum || '').toLowerCase();
      const name = (pop.vendorProduct?.product?.name || pop.vendorProduct?.product?.Name || pop.product?.name || pop.product?.Name || '').toLowerCase();
      return poNum.includes(f) || catalog.includes(f) || name.includes(f);
    });
  }

  displayPurchaseOrderProduct(pop: any): string {
    if (!pop) return '';
    const poNum = pop.purchaseOrder?.number || pop.purchaseOrder?.Number || '';
    const name = pop.vendorProduct?.product?.name || pop.vendorProduct?.product?.Name || pop.product?.name || pop.product?.Name || '';
    return poNum ? `${poNum} — ${name}` : name;
  }

  onPurchaseOrderProductSelected(index: number, pop: any): void {
    const purchaseOrderProductId = pop.id || pop.Id;
    const poNumber = pop.purchaseOrder?.number || pop.purchaseOrder?.Number || '';
    const productId = pop.vendorProduct?.product?.id || pop.product?.id || pop.product?.Id || '';
    const productName = pop.vendorProduct?.product?.name || pop.vendorProduct?.product?.Name || pop.product?.name || pop.product?.Name || '';
    const catalogNum = pop.vendorProduct?.product?.catalogNum || pop.vendorProduct?.product?.CatalogNum || pop.product?.catalogNum || pop.product?.CatalogNum || '';
    const vendorCatalogNum = pop.vendorProduct?.catalogNum || pop.vendorProduct?.CatalogNum || '';
    const vendorLotNum = pop.lotNum?.vendorLotNum || pop.LotNum?.VendorLotNum || '';
    const lotNumber = pop.lotNum?.number || pop.LotNum?.Number || '';
    const unitType = pop.unitType || pop.UnitType || '';
    const unitPrice = pop.unitPrice || pop.UnitPrice || 0;
    const quantityAvailable = pop.quantityAvailable ?? pop.QuantityAvailable ?? null;
    const desiredPackaging = pop.desiredPackaging || pop.DesiredPackaging || '';
    const description = pop.vendorProduct?.product?.description || pop.vendorProduct?.product?.Description || pop.product?.description || pop.product?.Description || '';
    this.products.at(index).patchValue({ purchaseOrderProductId, poNumber, lotNumId: '', lotNumber, productId, productName, catalogNum, vendorCatalogNum, vendorLotNum, unitType, unitPrice, quantityAvailable, desiredPackaging, description });
  }

  clearPurchaseOrderProduct(index: number): void {
    this.products.at(index).patchValue({
      purchaseOrderProductSearch: '', purchaseOrderProductId: '', poNumber: '', productId: '', productName: '',
      catalogNum: '', vendorCatalogNum: '', vendorLotNum: '', lotNumber: '', quantityAvailable: null, description: ''
    });
  }

  convertToInvoice(): void {
    if (!this.proformaId) return;

    this.conversionError = null;
    this.isConverting = true;
    this.apiService.post<any>(`proforma/${this.proformaId}/convert-to-invoice`, {}).subscribe({
      next: () => {
        this.isConverting = false;
        this.router.navigate(['/sales-invoices', this.proformaId]);
      },
      error: (err) => {
        this.isConverting = false;
        this.conversionError = err?.error?.error || 'Error converting proforma to invoice. Ensure all line items have a Lot Number assigned.';
        console.error('Error converting proforma to invoice:', err);
      }
    });
  }

  calculateNetAmount(index: number): number {
    const lineTotal = this.calculateLineTotal(index);
    const product = this.products.at(index);
    const discountAmount = product.get('discountAmount')?.value || 0;
    return lineTotal - discountAmount;
  }

  calculateLineTotalWithTax(index: number): number {
    const netAmount = this.calculateNetAmount(index);
    const product = this.products.at(index);
    const taxAmount = product.get('taxAmount')?.value || 0;
    return netAmount + taxAmount;
  }

  calculateSubTotal(): number {
    let subTotal = 0;
    for (let i = 0; i < this.products.length; i++) {
      subTotal += this.calculateLineTotal(i);
    }
    return subTotal;
  }

  calculateTotalAfterDiscounts(): number {
    let total = 0;
    for (let i = 0; i < this.products.length; i++) {
      total += this.calculateNetAmount(i);
    }
    const invoiceDiscountAmount = this.proformaForm.get('discountAmount')?.value || 0;
    return total - invoiceDiscountAmount;
  }

  calculateTotal(): number {
    const subTotal = this.calculateSubTotal();
    const discountAmount = this.proformaForm.get('discountAmount')?.value || 0;
    const taxAmount = this.proformaForm.get('taxAmount')?.value || 0;
    const shippingAmount = this.proformaForm.get('shippingAmount')?.value || 0;
    const handlingFee = this.proformaForm.get('handlingFee')?.value || 0;
    return subTotal - discountAmount + taxAmount + shippingAmount + handlingFee;
  }

  calculateBalance(): number {
    const totalAmount = this.calculateTotal();
    const amountPaid = this.proformaForm.get('amountPaid')?.value || 0;
    return totalAmount - amountPaid;
  }

  onSubmit() {
    if (this.proformaForm.valid) {
      const formValue = this.proformaForm.value;

      var proformaData: any = {
        Number: formValue.proformaNumber,
        Date: toUtcIsoDate(formValue.proformaDate),
        DueDate: toUtcIsoDate(formValue.dueDate),
        Status: formValue.status,
        CustomerId: formValue.customerId,
        ShippingAddressId: formValue.shippingAddressId,
        BillingAddressId: formValue.billingAddressId,
        CustomerContactId: formValue.customerContactId,
        CustomerPO: formValue.customerPO,
        SalesOrderId: formValue.salesOrderId,
        SubTotal: this.calculateSubTotal(),
        DiscountAmount: formValue.discountAmount,
        TaxAmount: formValue.taxAmount,
        ShippingAmount: formValue.shippingAmount,
        HandlingFee: formValue.handlingFee,
        TotalAmount: this.calculateTotal(),
        AmountPaid: formValue.amountPaid,
        PaymentTerms: formValue.paymentTerms,
        PaymentMethod: formValue.paymentMethod,
        Terms: formValue.terms,
        ExternalNotes: formValue.notes,
        ShipDate: toUtcIsoDate(formValue.shipDate),
        Shipper: formValue.shipper,
        SalesPerson: formValue.salesPerson,
        ShipAttentionTo: formValue.shipAttentionTo,
        BankDetailsId: this.bankDetails?.id || this.bankDetails?.Id || null,
        RemitToId: this.remitToAddress?.id || this.remitToAddress?.Id || null,
        Products: formValue.products.map((p: any) => ({
          ProductId: p.productId,
          PurchaseOrderProductId: p.purchaseOrderProductId || null,
          LotNumId: p.lotNumId || null,
          UnitPrice: p.unitPrice,
          ...(formValue.status === 'Draft' && !p.unitType ? {} : { UnitType: p.unitType }),
          Quantity: p.quantity,
          LineNumber: p.lineNumber,
          DiscountPercent: p.discountPercent,
          DiscountAmount: p.discountAmount,
          TaxRate: p.taxRate,
          TaxAmount: p.taxAmount,
          Description: p.description,
          Notes: p.notes,
          TaxCode: p.taxCode,
          DesiredPackaging: p.desiredPackaging || ''
        })),
        PurchaseOrders: this.purchaseOrders.map((po: any) => ({
          ...(po.id ? { Id: po.id } : {}),
        })),
        SalesOrders: this.salesOrders.map((so: any) => ({
          ...(so.id ? { Id: so.id } : {}),
        })),
      };

      console.log('Form submitted:', proformaData);
      this.errorMessage = '';

      if (this.proformaId) {
        proformaData.Id = this.proformaId;
        this.apiService.put<any>(`proforma/${this.proformaId}`, proformaData).subscribe({
          next: () => this.router.navigate(['/proformas']),
          error: (err) => {
            console.error('Error updating proforma:', err);
            this.showError('Failed to save proforma. Please try again.', err);
          }
        });
      } else {
        this.apiService.post<any>('proforma', proformaData).subscribe({
          next: () => this.router.navigate(['/proformas']),
          error: (err) => {
            console.error('Error creating proforma:', err);
            this.showError('Failed to create proforma. Please try again.', err);
          }
        });
      }
    }
  }

  onCancel() {
    if (this.proformaId) {
      this.isEditMode = false;
      this.loadProforma(this.proformaId);
    } else {
      this.router.navigate(['/proformas']);
    }
  }

  downloadPdf(): void {
    this.apiService.getBlob(`proforma/${this.proformaId}/pdf`).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const number = this.proformaForm.get('proformaNumber')?.value || this.proformaId;
        a.download = `${number}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading PDF:', err);
        this.showError('Failed to generate PDF. Please try again.', err);
      }
    });
  }

  loadRelatedSalesOrders(id: number): void {
    this.apiService.get<any>(`proforma/${id}/salesorders`).subscribe({
      next: (data) => {
        // Handle response as either a plain array or a wrapper object
        const rawOrders: any[] = Array.isArray(data)
          ? data
          : (data.salesOrders || data.SalesOrders || []);

        this.salesOrders = rawOrders.map((so: any) => ({
          id: so.id || so.Id,
          number: so.number || so.Number || '',
          date: so.date || so.Date ? new Date(so.date || so.Date) : null,
          status: so.status || so.Status || '',
          totalAmount: so.totalAmount || so.TotalAmount || 0
        }));

        // Collect purchase orders: top-level data.purchaseOrders AND nested inside sales orders
        const poMap = new Map<number, any>();
        const addPO = (po: any) => {
          const poId = po.id || po.Id;
          if (poId && !poMap.has(poId)) {
            poMap.set(poId, {
              id: poId,
              number: po.number || po.Number || '',
              date: po.date || po.Date ? new Date(po.date || po.Date) : null,
              status: po.status || po.Status || '',
              totalAmount: po.totalAmount || po.TotalAmount || 0
            });
          }
        };

        // Top-level purchase orders (when endpoint returns a wrapper object)
        const topLevelPOs: any[] = (!Array.isArray(data) && (data.purchaseOrders || data.PurchaseOrders)) || [];
        topLevelPOs.forEach(addPO);

        // Purchase orders nested inside each sales order
        rawOrders.forEach((so: any) => {
          (so.purchaseOrders || so.PurchaseOrders || []).forEach(addPO);
        });

        this.purchaseOrders = Array.from(poMap.values());

        // Collect sales invoices: top-level data.salesInvoices AND nested inside each sales order
        const invMap = new Map<number, any>();
        const addInvoice = (inv: any) => {
          const invId = inv.id || inv.Id;
          if (invId && !invMap.has(invId)) {
            invMap.set(invId, {
              id: invId,
              number: inv.invoiceNumber || inv.InvoiceNumber || inv.number || inv.Number || '',
              date: inv.invoiceDate || inv.InvoiceDate || inv.date || inv.Date
                ? new Date(inv.invoiceDate || inv.InvoiceDate || inv.date || inv.Date)
                : null,
              status: inv.status || inv.Status || '',
              totalAmount: inv.totalAmount || inv.TotalAmount || 0
            });
          }
        };

        // Top-level invoices (when endpoint returns a wrapper object)
        const topLevelInvoices: any[] = (!Array.isArray(data) && (data.salesInvoices || data.SalesInvoices || data.invoices || data.Invoices)) || [];
        topLevelInvoices.forEach(addInvoice);

        // Invoices nested inside each sales order
        rawOrders.forEach((so: any) => {
          (so.salesInvoices || so.SalesInvoices || so.invoices || so.Invoices || []).forEach(addInvoice);
        });

        this.salesInvoices = Array.from(invMap.values());

        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading related records:', err)
    });
  }

  viewSalesOrder(id: number): void {
    this.router.navigate(['/orders', id]);
  }

  viewPurchaseOrder(id: number): void {
    this.router.navigate(['/purchase-orders', id]);
  }

  viewSalesInvoice(id: number): void {
    this.router.navigate(['/sales-invoices', id]);
  }

  loadBankDetails(): void {
    this.apiService.get<any>('proforma/bankdetails').subscribe({
      next: (data) => {
        this.bankDetails = data;
        if (this.bankDetails) {
          this.proformaForm.patchValue({
            bankDetails: {
              id: this.bankDetails.id || this.bankDetails.Id || '',
              accountName: this.bankDetails.accountName || this.bankDetails.AccountName || '',
              bankName: this.bankDetails.bankName || this.bankDetails.BankName || '',
              accountNumber: this.bankDetails.accountNumber || this.bankDetails.AccountNumber || '',
              routingNumber: this.bankDetails.routingNumber || this.bankDetails.RoutingNumber || '',
              swiftCode: this.bankDetails.swiftCode || this.bankDetails.SwiftCode || '',
              fedwireABA: this.bankDetails.fedwireABA || this.bankDetails.FedwireABA || '',
              ibanCode: this.bankDetails.ibanCode || this.bankDetails.IBANCode || '',
              bankAddress: this.bankDetails.bankAddress || this.bankDetails.BankAddress || '',
              isActive: this.bankDetails.isActive !== undefined ? this.bankDetails.isActive : (this.bankDetails.IsActive !== undefined ? this.bankDetails.IsActive : true),
              notes: this.bankDetails.notes || this.bankDetails.Notes || ''
            }
          }, { emitEvent: false });
        }
      },
      error: (err) => { console.error('Error loading bank details:', err); this.bankDetails = null; }
    });
  }

  loadRemitToAddress(): void {
    this.apiService.get<any>('proforma/remittoaddress').subscribe({
      next: (data) => {
        this.remitToAddress = data;
        if (this.remitToAddress) {
          this.proformaForm.patchValue({
            remitToAddress: {
              addressLine1: this.remitToAddress.addressLine1 || this.remitToAddress.AddressLine1 || '',
              addressLine2: this.remitToAddress.addressLine2 || this.remitToAddress.AddressLine2 || '',
              city: this.remitToAddress.city || this.remitToAddress.City || '',
              state: this.remitToAddress.state || this.remitToAddress.State || '',
              postalCode: this.remitToAddress.postalCode || this.remitToAddress.PostalCode || '',
              country: this.remitToAddress.country || this.remitToAddress.Country || ''
            }
          }, { emitEvent: false });
        }
      },
      error: (err) => { console.error('Error loading remit to address:', err); this.remitToAddress = null; }
    });
  }

  formatAddress(address: any): string {
    const parts = [
      address.addressLine1 || address.AddressLine1,
      address.addressLine2 || address.AddressLine2,
      address.addressLine3 || address.AddressLine3,
      address.addressLine4 || address.AddressLine4,
      address.addressLine5 || address.AddressLine5,
      address.city || address.City,
      address.state || address.State,
      address.postalCode || address.PostalCode,
      address.country || address.Country
    ].filter(Boolean);
    return parts.join(', ') || '-';
  }

  getAddressTooltip(id: any, addresses: any[]): string {
    if (!id) return '';
    const found = addresses.find(a => (a.id || a.Id) == id);
    return found ? this.formatAddress(found) : '';
  }
}
